import { useEffect, useState } from "react";
import SideMenu from "../General/SideMenu";
import ReactTable from "../General/ReactTable";
import { useDispatch } from "react-redux";
import { Show_MsgBox, setload_Screen } from "../ReduxStore/Message";
import { userApp } from "../Admin/Utility";


// RptId map -- purchase-side GSTR2 views, already exist in rb_tbl_reports
const RPT = {
  B2B: 114,
  B2BUR: 115,
  CDNR: 117,
  HSN: 118,
};

// Field names confirmed from actual view SQL (rb_vw_tbl_report_gstr2b2bur,
// rb_vw_tbl_report_gstr2cdnr). B2B (114) assumed same structure as B2BUR
// (115) since both come from the same purchase invoice source -- confirm
// with a sample response if totals look off. HSN (118) not confirmed yet.
const CATEGORY_FIELD_MAP = {
  B2B: {
    confirmed: false, // same shape as B2BUR below, not independently confirmed
    taxable: "TaxableAmt",
    itcFields: ["ITCIgst", "ITCCgst", "ITCSGst", "ITCCess"],
    eligibility: "ITCEligibility",
    invoiceNo: "PurInvNo",
    partyName: "SuppName",
  },
  B2BUR: {
    confirmed: true,
    taxable: "TaxableAmt",
    itcFields: ["ITCIgst", "ITCCgst", "ITCSGst", "ITCCess"],
    eligibility: "ITCEligibility",
    invoiceNo: "PurInvNo",
    partyName: "SuppName",
  },
  CDNR: {
    confirmed: true,
    taxable: "TaxableAmt",
    itcFields: ["ITCIgst", "ITCCgst", "ITCSGst", "ITCCess"],
    eligibility: "ITCEligibility",
    invoiceNo: "NoteNo",
    partyName: "PartyName",
  },
  HSN: {
    confirmed: false,
    taxable: "TaxableAmt",
    itcFields: [],
    eligibility: null,
    invoiceNo: null,
    partyName: null,
  },
};

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function Gstr2b() {
  const dispatch = useDispatch();

  const today = new Date();
  const [iMonth, setiMonth] = useState(today.getMonth() + 1);
  const [iYear, setiYear] = useState(today.getFullYear());
  const [strActiveCard, setstrActiveCard] = useState("B2B");

  const [objRaw, setobjRaw] = useState({ B2B: [], B2BUR: [], CDNR: [], HSN: [] });
  const [objDesign, setobjDesign] = useState({ B2B: [], B2BUR: [], CDNR: [], HSN: [] });
  const [objSummary, setobjSummary] = useState({
    B2B: { count: 0, taxable: 0, eligible: 0, ineligible: 0 },
    B2BUR: { count: 0, taxable: 0, eligible: 0, ineligible: 0 },
    CDNR: { count: 0, taxable: 0, eligible: 0, ineligible: 0 },
    HSN: { count: 0, taxable: 0, eligible: 0, ineligible: 0 },
  });

  useEffect(() => {
    Load_GSTR2BData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [iMonth, iYear]);

  const AlertMessage = (Msg, type) => {
    dispatch(Show_MsgBox({ Message: Msg, Type: type, isMsg: true }));
  };

  const JsontoArray = (DataArray) => (DataArray !== null ? JSON.parse(DataArray) : []);
  const fnNum = (v) => {
    const n = Number(v);
    return isNaN(n) ? 0 : n;
  };

  // Pulls all 4 GSTR2B section reports -- same generic endpoint used for GSTR-1.
  const Load_GSTR2BData = async () => {
    try {
      await dispatch(setload_Screen(true));
      const strFromDate = `${iYear}-${String(iMonth).padStart(2, "0")}-01`;
      const objBase = {
        iPartyId: 0,
        strColName: "",
        strPartyName: "",
        strSearch: "",
        strCompanyDB: localStorage.db,
        strMasterDB: localStorage.Msdb,
        iUserId: Number(localStorage.UserId),
        CurrtPage: 1,
        strFromDate: strFromDate,
        iMonth: iMonth,
        iYear: iYear,
      };

      const keys = Object.keys(RPT);
      const calls = keys.map((key) =>
        userApp.post(`Reports/Load_GeneralReports/`, { ...objBase, iRptId: RPT[key] })
      );

      const results = await Promise.all(calls);
      const newRaw = {};
      const newDesign = {};
      const newSummary = {};

      results.forEach((Res, idx) => {
        const key = keys[idx];
        if (Res.data.strStatus === "Success") {
          const rows = JsontoArray(Res.data.EntReport);
          newRaw[key] = rows;
          newDesign[key] = (Res.data.EntDesign || []).sort((a, b) => a.inOrderTo - b.inOrderTo);
          newSummary[key] = Summarize_Rows(key, rows);
        } else {
          newRaw[key] = [];
          newDesign[key] = [];
          newSummary[key] = { count: 0, taxable: 0, eligible: 0, ineligible: 0 };
        }
      });

      setobjRaw(newRaw);
      setobjDesign(newDesign);
      setobjSummary(newSummary);
    } catch (e) {
      AlertMessage(e.message, "error");
    } finally {
      dispatch(setload_Screen(false));
    }
  };

  // Splits ITC into eligible / ineligible buckets based on the
  // ITCEligibility field, and totals taxable value.
  const Summarize_Rows = (key, rows) => {
    const map = CATEGORY_FIELD_MAP[key];
    let taxable = 0;
    let eligible = 0;
    let ineligible = 0;
    rows.forEach((r) => {
      taxable += fnNum(r[map.taxable]);
      const itcTotal = (map.itcFields || []).reduce((sum, f) => sum + fnNum(r[f]), 0);
      const isEligible = map.eligibility ? r[map.eligibility] === "Eligible" : false;
      if (isEligible) eligible += itcTotal;
      else ineligible += itcTotal;
    });
    return { count: rows.length, taxable, eligible, ineligible };
  };

  const fnFormatCurrency = (val) =>
    "\u20B9" + Number(val || 0).toLocaleString("en-IN", { maximumFractionDigits: 0 });

  const CARD_META = [
    { key: "B2B", label: "B2B purchases", icon: "fa-solid fa-building" },
    { key: "B2BUR", label: "B2B unregistered", icon: "fa-solid fa-user-slash" },
    { key: "CDNR", label: "Credit/debit notes", icon: "fa-solid fa-file-invoice" },
    { key: "HSN", label: "HSN summary", icon: "fa-solid fa-list" },
  ];

  // Same dynamic-column approach as Gstr1.js -- driven by EntDesign.
  const Build_Columns = (design) => {
    if (!design || design.length === 0) return [];
    return design
      .filter((d) => d.isShowInReport)
      .map((d) => ({
        Header: d.strAliasName,
        accessor: d.strRptColName,
        Datatype: d.strDatatype === "decimal" ? "Decimal" : undefined,
        minWidth: d.iRptColLen,
        style: { justifyContent: d.iFontAlign === 3 ? "right" : "left" },
      }));
  };

  // Totals across all 4 categories, for the top overview cards.
  const totalTaxable = Object.values(objSummary).reduce((s, o) => s + o.taxable, 0);
  const totalEligible = Object.values(objSummary).reduce((s, o) => s + o.eligible, 0);
  const totalIneligible = Object.values(objSummary).reduce((s, o) => s + o.ineligible, 0);
  const totalCount = Object.values(objSummary).reduce((s, o) => s + o.count, 0);

  const activeColumns = Build_Columns(objDesign[strActiveCard]);
  const activeMap = CATEGORY_FIELD_MAP[strActiveCard];

  return (
    <>
      <SideMenu ScrId={112} />
      <div className="gstr1-wrap">
        <div className="gstr1-header">
          <div>
            <h2 className="gstr1-title">GSTR-2B - purchase ITC</h2>
            <p className="gstr1-subtitle">View only -- reconcile input tax credit against your purchases</p>
          </div>
          <div className="gstr1-header-actions">
            <select className="form-control gstr1-period-select" value={iMonth} onChange={(e) => setiMonth(Number(e.target.value))}>
              {MONTHS.map((m, idx) => (<option key={idx} value={idx + 1}>{m}</option>))}
            </select>
            <select className="form-control gstr1-period-select" value={iYear} onChange={(e) => setiYear(Number(e.target.value))}>
              {[iYear - 1, iYear, iYear + 1].map((y) => (<option key={y} value={y}>{y}</option>))}
            </select>
          </div>
        </div>

        <div className="gstr1-card-grid">
          <div className="gstr1-card">
            <i className="fa-solid fa-file-lines"></i>
            <p className="gstr1-card-label">Total purchase records</p>
            <p className="gstr1-card-count">{totalCount}</p>
            <p className="gstr1-card-amount">{fnFormatCurrency(totalTaxable)} taxable</p>
          </div>
          <div className="gstr1-card">
            <i className="fa-solid fa-circle-check"></i>
            <p className="gstr1-card-label">Eligible ITC</p>
            <p className="gstr1-card-count">{fnFormatCurrency(totalEligible)}</p>
            <p className="gstr1-card-amount">can be claimed</p>
          </div>
          <div className="gstr1-card">
            <i className="fa-solid fa-circle-xmark"></i>
            <p className="gstr1-card-label">Ineligible ITC</p>
            <p className="gstr1-card-count">{fnFormatCurrency(totalIneligible)}</p>
            <p className="gstr1-card-amount">cannot be claimed</p>
          </div>
        </div>

        <div className="gstr1-tabs">
          {CARD_META.map((c) => (
            <div
              key={c.key}
              className={`gstr1-tab ${strActiveCard === c.key ? "active" : ""}`}
              onClick={() => setstrActiveCard(c.key)}
            >
              {c.label} ({objSummary[c.key].count})
            </div>
          ))}
        </div>

        {!activeMap.confirmed && (
          <div className="gstr1-alert-banner">
            <i className="fa-solid fa-triangle-exclamation"></i>
            Column names for "{strActiveCard}" are not fully confirmed yet -- share a sample API response for RptId {RPT[strActiveCard]} to verify totals.
          </div>
        )}

        <div className="gstr1-detail-table">
          <ReactTable
            columns={activeColumns}
            data={objRaw[strActiveCard] || []}
            minRows={5}
            row_click={() => {}}
            row_doubleclick={() => {}}
            background={true}
            className="full-table"
          />
        </div>
      </div>
    </>
  );
}

export default Gstr2b;